import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, map, startWith } from 'rxjs';

import { OnboardingEventTracker } from '../../engine/onboarding-event-tracker.service';
import { resolveLocalizedText } from '../../engine/localized-text';
import { matchesTutorialRoute } from '../../engine/route-match';
import { TutorialRunnerService } from '../../engine/tutorial-runner.service';
import { TutorialDefinition } from '../../models/onboarding.types';

interface HelpItem {
  key: string;
  title: string;
  minutes: number;
}

/** Tutoriais que começam na tela atual: o rota mais específica primeiro. */
export function tutorialsForUrl(tutorials: TutorialDefinition[], url: string): TutorialDefinition[] {
  return tutorials
    .map((tutorial) => ({ tutorial, route: tutorial.steps[0].route ?? '' }))
    .filter(({ route }) => route !== '' && matchesTutorialRoute(url, route))
    .sort((a, b) => b.route.length - a.route.length)
    .map(({ tutorial }) => tutorial);
}

/** Botão "?" do cabeçalho da página: some quando a tela não tem tutorial. */
@Component({
  selector: 'app-onboarding-help-button',
  standalone: true,
  imports: [MatIconModule, MatMenuModule, TranslateModule],
  templateUrl: './onboarding-help-button.component.html',
  styleUrl: './onboarding-help-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingHelpButtonComponent {
  private readonly router = inject(Router);
  private readonly runner = inject(TutorialRunnerService);
  private readonly tracker = inject(OnboardingEventTracker);
  private readonly translate = inject(TranslateService);

  private readonly tutorials = toSignal(this.runner.availableTutorials(), { initialValue: [] });
  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  readonly items = computed<HelpItem[]>(() =>
    tutorialsForUrl(this.tutorials(), this.url()).map((tutorial) => ({
      key: tutorial.key,
      title: resolveLocalizedText(tutorial.title, this.translate),
      minutes: tutorial.minutes,
    }))
  );

  onOpened(): void {
    this.tracker.track('help_opened', { context: { rota: this.url().split('?')[0] } });
  }

  start(item: HelpItem): void {
    this.runner.start(item.key, 'help_button');
  }
}
